import React, { useContext, useEffect, useRef, useState } from 'react'
import {
  HeaderContainer,
  InfoDiv,
  SearchDiv,
  SearchImg,
  SearchInput,
  StyledLink,
  MenuWrapper,
  MenuButton,
  DropdownMenu,
  DropdownItem,
  DangerDropdownItem,
  DropdownDivider,
  BellWrapper,
  BellButton,
  BellBadge,
  BellDropdownContainer,
  BellDropdownHeader,
  BellTab,
  BellDropdownBody,
  BellNotifItem,
  BellNotifAuthor,
  BellNotifComment,
  BellNotifTicket,
  BellEmptyState,
} from './style'
import ordenoLogo from '../../assets/sidemenu/ordeno-logo.svg'
import searchImg from '../../assets/headers/search.svg'
import UserContext from '../../contexts/UserContext'
import { TypeMainOutlet } from '../../pages/ClientPanel'
import { Bell, Bot, LogOut, Menu } from 'lucide-react'
import { useNotifications } from '../../contexts/NotificationContext'
import { useNavigate } from 'react-router-dom'

interface HeaderProps extends TypeMainOutlet {
  isChatOpen: boolean
  onToggleChat: () => void
}

const HeaderComponent: React.FC<HeaderProps> = ({
  search,
  setSearch,
  isChatOpen,
  onToggleChat,
}) => {
  const { user } = useContext(UserContext)
  const isTenantClient = (user?.role as string) === 'TENANT_CLIENT'
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isBellOpen, setIsBellOpen] = useState(false)
  const [bellTab, setBellTab] = useState<'unread' | 'all'>('unread')
  const menuRef = useRef<HTMLDivElement>(null)
  const bellRef = useRef<HTMLDivElement>(null)
  const { mentions, unreadCount, markRead } = useNotifications()
  const navigate = useNavigate()

  useEffect(() => {
    if (!isMenuOpen && !isBellOpen) return

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false)
      }
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setIsBellOpen(false)
      }
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false)
        setIsBellOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isMenuOpen, isBellOpen])

  const handleOpenChat = () => {
    if (!isChatOpen) onToggleChat()
    setIsMenuOpen(false)
  }

  const handleLogout = () => {
    localStorage.removeItem('token')
    window.location.href = '/login'
  }

  const handleNotifClick = (mention: typeof mentions[number]) => {
    if (!mention.viewed) {
      markRead([mention.id])
    }
    const ticket = mention.Comment?.Ticket
    if (ticket) {
      const pipelineId = ticket.Step?.pipelineId
      navigate(`/panel/pipeline/ticket?pipelineId=${pipelineId}&ticketId=${ticket.id}`)
    }
    setIsBellOpen(false)
  }

  const visibleMentions = bellTab === 'unread'
    ? mentions.filter((m) => !m.viewed)
    : mentions

  return (
    <HeaderContainer>
      {isTenantClient ? (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <img src={ordenoLogo} alt='Ordeno logo' draggable='false' />
        </div>
      ) : (
        <StyledLink to={'/panel'}>
          <img src={ordenoLogo} alt='Ordeno logo' draggable='false' />
        </StyledLink>
      )}

      {!isTenantClient && (
        <SearchDiv>
          <SearchInput
            type='text'
            autoComplete='off'
            placeholder='Buscar'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <SearchImg src={searchImg} alt='Search icon' />
        </SearchDiv>
      )}

      <InfoDiv>
        <img src={user.picture} draggable='false' />
        <span>{user.name}</span>

        <BellWrapper ref={bellRef}>
          <BellButton
            onClick={() => setIsBellOpen((v) => !v)}
            title='Notificações'
            aria-expanded={isBellOpen}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <BellBadge>{unreadCount > 99 ? '99+' : unreadCount}</BellBadge>
            )}
          </BellButton>

          {isBellOpen && (
            <BellDropdownContainer>
              <BellDropdownHeader>
                <BellTab $active={bellTab === 'unread'} onClick={() => setBellTab('unread')}>
                  Não lidas {unreadCount > 0 && `(${unreadCount})`}
                </BellTab>
                <BellTab $active={bellTab === 'all'} onClick={() => setBellTab('all')}>
                  Todas
                </BellTab>
              </BellDropdownHeader>
              <BellDropdownBody>
                {visibleMentions.length === 0 ? (
                  <BellEmptyState>
                    {bellTab === 'unread' ? 'Nenhuma notificação não lida.' : 'Nenhuma notificação.'}
                  </BellEmptyState>
                ) : (
                  visibleMentions.map((mention) => (
                    <BellNotifItem
                      key={mention.id}
                      $unread={!mention.viewed}
                      onClick={() => handleNotifClick(mention)}
                    >
                      <BellNotifAuthor>
                        {mention.Comment?.User?.name ?? 'Alguém'} mencionou você
                      </BellNotifAuthor>
                      <BellNotifComment>
                        {mention.Comment?.comment ?? ''}
                      </BellNotifComment>
                      {mention.Comment?.Ticket && (
                        <BellNotifTicket>
                          {mention.Comment.Ticket.title}
                        </BellNotifTicket>
                      )}
                    </BellNotifItem>
                  ))
                )}
              </BellDropdownBody>
            </BellDropdownContainer>
          )}
        </BellWrapper>

        <MenuWrapper ref={menuRef}>
          <MenuButton
            onClick={() => setIsMenuOpen((v) => !v)}
            title='Menu'
            aria-expanded={isMenuOpen}
            aria-haspopup='true'
          >
            <Menu size={20} />
          </MenuButton>
          {isMenuOpen && (
            <DropdownMenu>
              <DropdownItem $active={isChatOpen} onClick={handleOpenChat}>
                <Bot size={18} color='#0090D3' />
                Assistente Ordeno
              </DropdownItem>
              <DropdownDivider />
              <DangerDropdownItem onClick={handleLogout}>
                <LogOut size={18} />
                Sair
              </DangerDropdownItem>
            </DropdownMenu>
          )}
        </MenuWrapper>
      </InfoDiv>
    </HeaderContainer>
  )
}

export default HeaderComponent
